import {FC} from 'react'
import {Row, Col, Badge} from 'react-bootstrap'
import {CGESimulateResponse} from '../core/_models'

type CGEHeadlineCardsProps = {
  headline: CGESimulateResponse['headline']
  regions: CGESimulateResponse['regions']
}

const CGEHeadlineCards: FC<CGEHeadlineCardsProps> = ({headline, regions}) => {
  const welfareBadge = (value: number) => (
    <Badge className='ms-2 text-white fs-7' bg={value >= 0 ? 'success' : 'danger'}>
      US$ {value.toLocaleString()}M
    </Badge>
  )

  const pctBadge = (value: number) => (
    <Badge className='ms-2 text-white fs-7' bg={value >= 0 ? 'success' : 'danger'}>
      {value >= 0 ? '+' : ''}
      {value}%
    </Badge>
  )

  const focusRegion = regions.find((r) => r.code === headline.focus_code)

  return (
    <Row className='my-3'>
      <Col md={6} className='px-5 mb-5'>
        <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
          <div className='symbol symbol-45px me-5'>
            <i className='fa-solid fa-scale-balanced'></i>
          </div>
          <div className='d-flex flex-column'>
            <div className='text-gray-800 fs-6 fw-bold'>
              Welfare impact on {headline.focus_name}{' '}
              {welfareBadge(headline.focus_welfare_ev_usd_mn)}
            </div>
            <span className='text-gray-500 fw-semibold'>
              Terms of trade change: {headline.focus_terms_of_trade_pct}%
            </span>
          </div>
        </div>
      </Col>
      <Col md={6} className='px-5 mb-5'>
        <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
          <div className='symbol symbol-45px me-5'>
            <i className='fa-solid fa-industry'></i>
          </div>
          <div className='d-flex flex-column'>
            {headline.focus_most_affected_sector ? (
              <>
                <div className='text-gray-800 fs-6 fw-bold'>
                  Most affected sector: {headline.focus_most_affected_sector.name}
                  <Badge
                    className='ms-2 text-white fs-7'
                    bg={headline.focus_most_affected_sector.output_pct >= 0 ? 'success' : 'danger'}
                  >
                    {headline.focus_most_affected_sector.output_pct}%
                  </Badge>
                </div>
                <span className='text-gray-500 fw-semibold'>Output change vs baseline</span>
              </>
            ) : focusRegion ? (
              <>
                <div className='text-gray-800 fs-6 fw-bold'>
                  Real GDP change: {headline.focus_name}
                  {pctBadge(focusRegion.real_gdp_pct)}
                </div>
                <span className='text-gray-500 fw-semibold'>vs baseline</span>
              </>
            ) : (
              <span className='text-gray-500 fw-semibold'>
                Sector output detail not available for this solver
              </span>
            )}
          </div>
        </div>
      </Col>
      <Col md={6} className='px-5'>
        <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
          <div className='symbol symbol-45px me-5'>
            <i className='fa-solid fa-arrow-trend-down text-danger'></i>
          </div>
          <div className='d-flex flex-column'>
            <div className='text-gray-800 fs-6 fw-bold'>
              Economy with the largest welfare loss: {headline.largest_welfare_loss.name}{' '}
              {welfareBadge(headline.largest_welfare_loss.welfare_ev_usd_mn)}
            </div>
          </div>
        </div>
      </Col>
      <Col md={6} className='px-5'>
        <div className='d-flex align-items-center flex-grow-1 border border-2 border-gray-300 border-hover p-5'>
          <div className='symbol symbol-45px me-5'>
            <i className='fa-solid fa-arrow-trend-up text-success'></i>
          </div>
          <div className='d-flex flex-column'>
            <div className='text-gray-800 fs-6 fw-bold'>
              Economy with the largest welfare gain: {headline.largest_welfare_gain.name}{' '}
              {welfareBadge(headline.largest_welfare_gain.welfare_ev_usd_mn)}
            </div>
          </div>
        </div>
      </Col>
    </Row>
  )
}

export {CGEHeadlineCards}
